import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { GenericSelect } from '@/styles/GenericForm';
import { SaveChangesButton, SmallSecondaryButton } from '@/styles/StandardButtons';
import { useAdminGuard } from '../AdminOnlyGuard';
import { fetchBobSettings, saveBobSettings, type BobSettings } from './aiUsageApi';
import { REVIEW_SLOTS, formatInZone } from './slots';

/**
 * Settings > Usage > "Bob's daily review": the time of day Bob reviews the company, in the COMPANY's time zone (Settings > Company). The review itself is
 * mandatory -- there is no switch -- and the screen says truthfully whether the scheduler is running on the server.
 */
export function BobReviewScheduleSection() {
  const canApply = useAdminGuard();
  const [settings, setSettings] = useState<BobSettings | null>(null);
  const [hour, setHour] = useState('5');
  const [followUp, setFollowUp] = useState('24');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const apply = useCallback((next: BobSettings) => {
    setSettings(next);
    setHour(String(next.review_hour));
    setFollowUp(String(next.followup_after_hours));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await fetchBobSettings());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bob's schedule could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [apply]);
  useEffect(() => { void load(); }, [load]);

  const followUpNumber = Number(followUp);
  const followUpValid = Number.isInteger(followUpNumber) && followUpNumber >= 1 && followUpNumber <= 720;
  const dirty = !!settings && (Number(hour) !== settings.review_hour || (followUpValid && followUpNumber !== settings.followup_after_hours));

  const save = async () => {
    if (!canApply()) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      apply(await saveBobSettings({ review_hour: Number(hour), followup_after_hours: followUpNumber }));
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bob's schedule could not be saved");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="max-w-2xl" data-testid="bob-schedule">
      <CardHeader><CardTitle className="font-heading-text">Bob&apos;s daily review</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-gray-500">Loading schedule...</p>}
        {!loading && error && (
          <div className="space-y-2">
            <div className="error-message" role="alert">{error}</div>
            {!settings && <SmallSecondaryButton type="button" onClick={() => void load()}>Retry</SmallSecondaryButton>}
          </div>
        )}
        {!loading && settings && !settings.available && <p className="text-sm text-gray-500">Bob&apos;s schedule is not available yet.</p>}
        {!loading && settings && settings.available && (
          <>
            <p className="text-sm text-gray-600">
              Bob reviews your projects once a day, during the time you choose.
            </p>

            {settings.timezone_confirmed ? (
              <p className="text-sm text-gray-700">Time zone: <span className="font-medium">{settings.timezone}</span> <span className="text-gray-500">(change it in Settings &gt; Company)</span></p>
            ) : (
              <p className="text-sm text-amber-700" role="status">The company time zone is not set, so the review runs on UTC. Set it in Settings &gt; Company to have it run on your local time.</p>
            )}

            <div className="flex flex-wrap items-end gap-4">
              <div className="w-64">
                <label htmlFor="bob_review_hour" className="font-form-label block mb-1">Review hour</label>
                <GenericSelect id="bob_review_hour" value={hour} options={REVIEW_SLOTS} required disabled={saving} onValueChange={(v) => { setSaved(false); setHour(v); }} />
              </div>
              <div className="w-44">
                <label htmlFor="bob_followup_hours" className="font-form-label block mb-1">Unanswered vendor comment after (hours)</label>
                <Input id="bob_followup_hours" type="number" min={1} max={720} step={1} className="h-10" value={followUp} disabled={saving} onChange={(e) => { setSaved(false); setFollowUp(e.target.value); }} />
              </div>
            </div>
            {!followUpValid && <p className="text-xs text-red-700" role="alert">Enter a whole number of hours between 1 and 720.</p>}

            {settings.next_review_at && (
              <p className="text-sm text-gray-600" data-testid="next-review">
                Next review: <span className="font-medium">{formatInZone(settings.next_review_at, settings.review_timezone)}</span>
              </p>
            )}

            <div className={`rounded-md border px-3 py-2 text-sm ${settings.scheduler.running ? 'border-green-200 bg-green-50 text-green-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`} role="note" data-testid="scheduler-status">
              {settings.scheduler.message}
            </div>

            <div className="flex items-center gap-3">
              <SaveChangesButton type="button" onClick={() => void save()} disabled={!dirty || saving || !followUpValid}>{saving ? 'Saving…' : 'Save schedule'}</SaveChangesButton>
              {saved && !dirty && <span className="text-sm text-gray-500" role="status">Saved</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
